import { useEffect, useState } from 'preact/hooks';
import { type PendingFaceID, Vault } from '../app/vault';
import { BrandLockup, PinPad, PlainButton, PrimaryButton, Sheet } from './components';
import { FaceID as FaceIDIcon } from './icons';
import { useApp } from './state';

/** Shown at every opening: passcode, or Face ID / Touch ID when enabled. */
export function LockScreen() {
  const { s, model } = useApp();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [wait, setWait] = useState(Vault.lockoutRemaining());
  const faceID = Vault.hasFaceID();

  useEffect(() => {
    if (wait <= 0) return;
    const t = setInterval(() => setWait(Vault.lockoutRemaining()), 1000);
    return () => clearInterval(t);
  }, [wait > 0]);

  const tryFaceID = async (silent = false) => {
    setBusy(true);
    const ok = await model.unlockWithFaceID();
    setBusy(false);
    if (!ok && !silent) setError(s.faceIDFailed);
  };

  // Try Face ID as soon as the lock screen appears (some browsers need a tap: the button stays).
  useEffect(() => {
    if (faceID) void tryFaceID(true);
  }, []);

  if (forgot) {
    return (
      <Sheet title={s.pinForgot} left={<PlainButton onClick={() => setForgot(false)}>‹ {s.cancel}</PlainButton>}>
        <p>{s.pinForgotText}</p>
        <PrimaryButton class="danger-btn" onClick={() => model.forgetWallet(true)}>{s.pinReset}</PrimaryButton>
      </Sheet>
    );
  }

  return (
    <div class="screen pin-screen">
      <div class="lock-brand"><BrandLockup markSize={56} /></div>
      {faceID && (
        <PrimaryButton class="faceid-btn" onClick={() => void tryFaceID()}>
          <FaceIDIcon size={22} /> {s.unlockFaceID}
        </PrimaryButton>
      )}
      <PinPad
        title={s.pinEnterTitle}
        error={wait > 0 ? s.pinLocked(wait) : error}
        busy={busy}
        faceID={faceID}
        onFaceID={tryFaceID}
        onComplete={async (pin) => {
          if (Vault.lockoutRemaining() > 0) return;
          setBusy(true);
          const ok = await model.unlockWithPin(pin);
          setBusy(false);
          if (!ok) {
            const left = Vault.attemptsLeft();
            setWait(Vault.lockoutRemaining());
            setError(s.pinWrong(left === 5 ? 0 : left));
          }
        }}
        footer={<PlainButton class="dim small" onClick={() => setForgot(true)}>{s.pinForgot}</PlainButton>}
      />
    </div>
  );
}

/** Passcode confirmation for sensitive actions (send, show phrase, export, delete). */
export function AuthSheet() {
  const { s, model } = useApp();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const req = model.auth;
  if (!req) return null;
  return (
    <Sheet class="auth-sheet" right={<PlainButton onClick={() => req.resolve(false)}>{s.cancel}</PlainButton>}>
      <PinPad
        title={s.confirmWithPin}
        subtitle={req.reason}
        error={Vault.lockoutRemaining() > 0 ? s.pinLocked(Vault.lockoutRemaining()) : error}
        busy={busy}
        onComplete={async (pin) => {
          setBusy(true);
          const ok = await Vault.checkPin(pin);
          setBusy(false);
          if (ok) req.resolve(true);
          else setError(s.pinWrong(Vault.attemptsLeft() === 5 ? 0 : Vault.attemptsLeft()));
        }}
      />
    </Sheet>
  );
}

/** Covers the app in the app switcher / when the page is hidden. */
export function PrivacyCover() {
  return (
    <div class="privacy-cover" aria-hidden="true">
      <BrandLockup markSize={88} />
    </div>
  );
}

/** Offered once, right after the passcode is chosen. */
export function FaceIDOffer() {
  const { s, model } = useApp();
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<PendingFaceID | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pin = model.faceIDOfferPin;

  // Each step starts directly from a tap: browsers show Face ID only for a user action.
  const finish = async (p: PendingFaceID) => {
    if (!pin) return;
    setBusy(true);
    const ok = await model.finishFaceID(pin, p).catch(() => false);
    setBusy(false);
    if (ok) model.declineFaceIDOffer();
    else { setPending(null); setError(s.faceIDFailed); }
  };
  const start = async () => {
    setError(null);
    setBusy(true);
    const p = await Vault.startFaceID().catch(() => null);
    setBusy(false);
    if (!p) return setError(s.faceIDFailed);
    if (p.output) await finish(p);
    else setPending(p); // one more Face ID tap is needed on this browser
  };
  return (
    <Sheet>
      <div class="stack-20 grow text-center center-col">
        <div class="spacer" />
        <span class="icon-circle huge"><FaceIDIcon size={30} /></span>
        <h1 class="large-title">{s.faceIDOfferTitle}</h1>
        <p class="dim">{s.faceIDOfferText}</p>
        <div class="spacer" />
        {error && <p class="error">{error}</p>}
        {pending
          ? <PrimaryButton disabled={busy} onClick={() => void finish(pending)}><FaceIDIcon size={20} /> {s.faceIDConfirm}</PrimaryButton>
          : <PrimaryButton disabled={busy} onClick={() => void start()}><FaceIDIcon size={20} /> {s.faceIDOfferButton}</PrimaryButton>}
        <PlainButton onClick={() => model.declineFaceIDOffer()}>{s.later}</PlainButton>
      </div>
    </Sheet>
  );
}
